/**
 * User tier resolution.
 *
 * Derives a coarse-grained "tier" from the current user's RBAC permissions so the
 * dashboard can render a different experience per tier (e.g. a different Cases page).
 *
 * This is intentionally permission-based (not role-name based) so it stays robust to
 * role renames. It is also a pure function of the permission list so it can be unit
 * tested and reused outside React.
 *
 * To add a new tier: add an entry to TIER_RULES (higher in the list = higher priority)
 * and register a page component in the relevant tier registry.
 */

import type { UserRole } from '@compass/client/types/membership';
import type { Permission } from '@compass/client/types/role';

export type UserTier = 'tier2' | 'standard';

interface TierRule {
  tier: UserTier;
  matches: (perms: Permission[]) => boolean;
}

// RBAC wildcard semantics (see permission.proto): a permission with
// resource "all" grants access to any resource, and type "all" to any action.
// So admin-style permissions (`all:all:*`) must satisfy a `case_record:write`
// requirement even though the strings don't match literally.
const matchesResource = (resource?: string): boolean =>
  resource === 'case_record' || resource === 'all';
const matchesWrite = (type?: string): boolean => type === 'write' || type === 'all';

/**
 * Ordered by priority — the first matching rule wins. The implicit fallback is 'standard'.
 */
const TIER_RULES: TierRule[] = [
  {
    tier: 'tier2',
    // Has case-record write (any scope), including via the `all` resource/action
    // wildcards held by System/Organization admins.
    matches: perms => perms.some(p => matchesResource(p.resource) && matchesWrite(p.type)),
  },
];

/**
 * Collect the permissions that apply org-wide for tier resolution: every role
 * scoped to the given organization (org-level AND any program-level role within
 * it) plus global roles. Unlike per-program context filtering
 * ({@link import('@/utils/user').filterRolesByContext}), this does NOT drop
 * program-level roles for other programs in the org — so a user who has write in
 * any one program resolves to the same tier across the org-wide experience.
 */
export function collectOrgPermissions(
  roles: UserRole[],
  organizationId?: string | null,
): Permission[] {
  return roles
    .filter(
      r =>
        (!!organizationId && r.organization_id === organizationId) ||
        (!r.organization_id && !r.program_id),
    )
    .flatMap(r => r.permissions || []);
}

/**
 * Resolve the user's tier from their (context-filtered) permissions.
 *
 * @param permissions Flat permission list, e.g. from `useCurrentUserContextRoles().permissions`.
 * @returns The highest-priority matching tier, or 'standard' if none match.
 */
export function resolveUserTier(permissions: Permission[]): UserTier {
  for (const rule of TIER_RULES) {
    if (rule.matches(permissions)) {
      return rule.tier;
    }
  }
  return 'standard';
}

/**
 * Fold eligibility + the user's opt-in into the *effective* tier the app renders.
 *
 * The Org-wide Cases experience is opt-in AND PQC-scoped: it takes effect only
 * when the user is eligible — has case-record write AND the selected org exposes
 * PQC workflows to them (see `useUserTier`) — and has turned the flag on. Everyone
 * else (write-less users, non-PQC orgs, or flag off) stays 'standard', so the
 * entire feature is confined to PQC flows.
 *
 * Every tier gating site consumes this via `useUserTier`, so the whole app reverts
 * to the previous experience the instant the flag is off or the org isn't PQC.
 *
 * @param isEligible Whether the user can use the feature (permission tier2 + PQC org).
 * @param orgWideCasesEnabled The user's opt-in preference.
 */
export function resolveEffectiveTier(isEligible: boolean, orgWideCasesEnabled: boolean): UserTier {
  return isEligible && orgWideCasesEnabled ? 'tier2' : 'standard';
}
